import { useEffect, useMemo } from 'react';
import { useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';

import { useInsightsParams } from './useInsightsParams';
import { GraphqlQueryDataResponse } from '@insights_module/types/graphql/ClientTypes';
import { useMyLibraryContext } from '@insights_module/context/MyLibraryContext';
import { graphqlApi } from '@configs/axios-instances';
import { AQLQueryValue } from '@insights_module/adc_ql/types';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
  AQLTitleQuery,
} from '@insights_module/adc_ql/queries';
import { tokenToObject } from '@utils/commonUtils';

const libraryAudiosGraphQLQuery = `query Audios($pageSize: String, $pageNo: String, $sortField: String, $sortOrder: String $payload: String) {
    audios(page_size: $pageSize, page_no: $pageNo, sort_field: $sortField, sort_order: $sortOrder, payload: $payload) {
      data {
        id
        title
        duration
        client_duration
        original_url
        hosted_url
        language
        skip_processing
        external_ids {
          name
          value
        }
        published_date
        created_at
        updated_at
        details {
          id
          title
          duration
          client_duration
          original_url
          hosted_url
          language
          skip_processing
          groups {
            id
            name
            type
            thumbnail_url
            tenant_id
            audio_count
            most_recently_published_audio_date
            extra_fields {
              name
              value
            }
            podcast_metadata {
              apple_categories
              apple_rating_count
              apple_star_rating
              author
              description
              podcast_type
            }
            created_at
            updated_at
          }
          published_date
          created_at
          updated_at
        }
        garm {
          adult_explicit_content
          arms_ammunition
          crime_harmful_act
          death_injury_military_conflict
          debated_social
          illegal_drugs
          hate_speech
          obscenity
          online_piracy
          spam_harmful
          terrorism
        }
        contextual {
          id
          name
          display_name
          score
          taxonomy {
            taxonomy
            name
            tier
            id
            parent_id
          }
        }
      }
      pagination {
        total
        page_no
        page_size
        page_token
      }
    }
  }`;

const PAGE_SIZE = '20';

export const useLibraryGraphqlAudios = () => {
  const queryClient = useQueryClient();
  const { audiosTableSort, tableType } = useMyLibraryContext();
  const { searchTerm, queryToken } = useInsightsParams();

  const aqlQuery = useMemo(() => {
    const queryObject = tokenToObject(queryToken as string);
    const query = {
      all: [
        new AQLBrandSafetyGarmQuery([
          new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
            gte: 'unrelated',
            lte: 'floor',
          }),
        ]),
      ] as AQLQueryValue[],
    };

    // if we have queryObject but it is not an empty array
    if (
      tableType === 'audios' &&
      queryObject &&
      typeof queryObject === 'object' &&
      !(Array.isArray(queryObject) && !queryObject.length)
    ) {
      query.all.push(queryObject);
    }

    if (searchTerm) {
      query.all.push(new AQLTitleQuery('includes', searchTerm as string));
    }

    return JSON.stringify({ query });
  }, [searchTerm, queryToken, tableType]);

  const variables = useMemo(
    () => ({
      ...audiosTableSort,
      pageSize: PAGE_SIZE,
      payload: aqlQuery,
    }),
    [audiosTableSort, PAGE_SIZE, aqlQuery]
  );

  const { data, isLoading, isFetching, isPreviousData } = useQuery({
    queryKey: ['graphql', 'graphql-library-audios', variables],
    queryFn: async () => graphqlApi.post('', { query: libraryAudiosGraphQLQuery, variables }),
    keepPreviousData: true,
    staleTime: 60 * 60 * 1000, // 1 hour
  }) as { data: GraphqlQueryDataResponse } & UseQueryResult<unknown, unknown>;

  useEffect(() => {
    // If we have more data we will try to prefetch the next page.
    if (!data?.data?.audios?.pagination?.total) {
      return;
    }

    const hasMore =
      Number(PAGE_SIZE) * Number(variables.pageNo) < data?.data.audios?.pagination?.total;

    const prefetchVariables = { ...variables, pageNo: String(Number(variables.pageNo) + 1) };

    if (!isPreviousData && hasMore) {
      queryClient.prefetchQuery({
        queryKey: ['graphql', 'graphql-library-audios', prefetchVariables],
        queryFn: async () =>
          graphqlApi.post('', {
            query: libraryAudiosGraphQLQuery,
            variables: prefetchVariables,
          }),
      });
    }
  }, [data, isPreviousData, variables, queryClient]);

  return {
    data: data?.data?.audios?.data,
    pagination: data?.data?.audios?.pagination,
    total: data?.data?.audios?.pagination?.total,
    isLoading,
    isFetching,
    isPrefetching: isFetching,
  };
};
